import { type FC, useState } from "react";

import { Button } from "@jti/core-components/components/Button";
import type { AddressStored } from "@jti/fe-common/types";
import { AddressSelectCard, Dialog } from "@jti/ui";
import { useDeviceBreakpoint } from "@jti/ui/hooks/useDeviceBreakpoint/useDeviceBreakpoint";
import { useTranslations } from "@jti/ui/providers/useTranslations";

import styles from "../../AtHomeAddressSelectionDialog.module.scss";

export type AddressSelectionDialogProps = {
  addresses: AddressStored[];
  preselectedAddress: AddressStored;
  onCreateNew: () => void;
  onClose: () => void;
  onConfirm: (address: AddressStored) => void;
  isOnSubmitLoading?: boolean;
};

export const AddressSelectionDialog: FC<AddressSelectionDialogProps> = ({
  addresses,
  onCreateNew,
  onClose,
  onConfirm,
  preselectedAddress,
  isOnSubmitLoading = false,
}) => {
  const { __ } = useTranslations();
  const [selectedAddress, setSelectedAddress] = useState(preselectedAddress);
  const { breakpoint } = useDeviceBreakpoint();

  return (
    <Dialog
      closable
      isOpened
      fullscreen={breakpoint === "mobile"}
      className={styles.dialog}
      dialogClassName={styles.dialogContainer}
      onClose={onClose}
    >
      <div className={styles.content}>
        <div className={styles.header}>
          <h2 className={styles.title}>{__("cart.checkout.shipping_address.select_from_existing_dialog.title")}</h2>
          <h3 className={styles.subtitle}>
            {__("cart.checkout.shipping_address.select_from_existing_dialog.subtitle")}
          </h3>
        </div>

        <div
          className={styles.cards}
          role="radiogroup"
          aria-label={__("cart.checkout.shipping_address.select_from_existing_dialog.title")}
        >
          {addresses.map((address) => (
            <AddressSelectCard
              telephonePosition="top"
              key={address.id}
              address={address}
              onSelect={() => setSelectedAddress(address)}
              isSelected={selectedAddress.id === address.id}
            />
          ))}
          <span className={styles.mobileButtonWrapper}>
            <Button variant="text" onClick={onCreateNew}>
              {__("cart.checkout.shipping_address.select_from_existing_dialog.create_new")}
            </Button>
          </span>
        </div>

        <span className={styles.desktopButtonWrapper}>
          <Button variant="text" onClick={onCreateNew} size="lg">
            {__("cart.checkout.shipping_address.select_from_existing_dialog.create_new")}
          </Button>
        </span>

        <div className={styles.footer}>
          <span className={styles.mobileButtonWrapper}>
            <Button
              variant="primary"
              size="sm"
              fullWidth
              onClick={() => {
                onConfirm(selectedAddress);
              }}
              pending={isOnSubmitLoading}
            >
              {__("cart.checkout.shipping_address.select_from_existing_dialog.select")}
            </Button>
          </span>
          <span className={styles.desktopButtonWrapper}>
            <Button
              variant="primary"
              size="lg"
              onClick={() => {
                onConfirm(selectedAddress);
              }}
              pending={isOnSubmitLoading}
            >
              {__("cart.checkout.shipping_address.select_from_existing_dialog.select")}
            </Button>
          </span>
        </div>
      </div>
    </Dialog>
  );
};
